import React, { useState } from 'react';
import { Card } from '@core-ui/Components/ui/card.tsx';
import { Badge } from '@core-ui/Components/ui/badge.tsx';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { Checkbox } from '@core-ui/Components/ui/checkbox.tsx';
import { IconInfoCircle, IconRefresh } from '@tabler/icons-react';
import { Check, Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { IntegrationJob } from '@features/dashboard/types/dashboard.types.ts';
import { cn } from '@core-ui-lib/utils.ts';
import { Tooltip, TooltipContent, TooltipTrigger } from '@core-ui/Components/ui/tooltip.tsx';

interface Props {
  job: IntegrationJob;
  showRetry?: boolean;
  isRetrying?: boolean;
  onRetry?: (jobId: string, includeSkipped: boolean) => Promise<void>;
}

const statusClass: Record<string, string> = {
  FAILED: 'bg-red-500/10 text-red-700 border-red-500/30 dark:text-red-300',
  SKIPPED: 'bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300',
};

const KNOWN_REASON_KEYS = new Set([
  'meta_write_failed',
  'missing_meta_or_phone',
  'missing_externalId',
  'no_matching_disposition',
  'createCall_threw',
  'cant_create_call',
]);

function resolveReasonKey (reason: string): string {
  const withoutTestPrefix = reason.startsWith('TEST_SEED_')
    ? reason.slice('TEST_SEED_'.length)
    : reason;

  if (KNOWN_REASON_KEYS.has(withoutTestPrefix)) {
    return withoutTestPrefix;
  }

  if (withoutTestPrefix.startsWith('Can\'t create call for ')) {
    return 'cant_create_call';
  }

  return withoutTestPrefix;
}

function formatSyncReason (
  reason: string | null | undefined,
  t: (key: string, options?: { defaultValue?: string }) => string,
): string | null {
  if (reason == null || reason === '') {
    return null;
  }

  const reasonKey = resolveReasonKey(reason);
  return t(`dashboard.integrations.jobLogs.reasons.${reasonKey}`, {
    defaultValue: reasonKey === reason ? reason : reason.replace(/^TEST_SEED_/, ''),
  });
}

function BadgeWithTooltip ({
  label,
  tooltip,
  className,
}: {
  label: string;
  tooltip: string;
  className?: string;
}): React.JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant="outline" className={cn('text-xs', className)}>
          {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px]">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

export default function JobLogs ({
  job,
  showRetry = false,
  isRetrying = false,
  onRetry,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [includeSkipped, setIncludeSkipped] = useState(false);
  const [copiedContactId, setCopiedContactId] = useState<string | null>(null);
  const errors = job.syncErrors ?? [];
  const counts = job.counts ?? { success: 0, failed: 0, skipped: 0 };
  const hasFailed = counts.failed > 0;
  const hasSkipped = counts.skipped > 0;
  const canRetry = showRetry && (hasFailed || (includeSkipped && hasSkipped));

  const handleCopyContactId = async (contactId: string): Promise<void> => {
    await navigator.clipboard.writeText(contactId);
    setCopiedContactId(contactId);
    setTimeout(() => {
      setCopiedContactId(null);
    }, 1500);
  };

  return (
    <Card className="gap-2 rounded-xl bg-muted/40 border border-border mt-2 mb-2 mx-2 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <IconInfoCircle className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {t('dashboard.integrations.jobLogs.title')}
          </span>
          <BadgeWithTooltip
            label={`${t('dashboard.integrations.jobLogs.success')}: ${counts.success}`}
            tooltip={t('dashboard.integrations.jobLogs.tooltips.success')}
          />
          <BadgeWithTooltip
            label={`${t('dashboard.integrations.jobLogs.failed')}: ${counts.failed}`}
            tooltip={t('dashboard.integrations.jobLogs.tooltips.failed')}
            className={cn('border', statusClass.FAILED)}
          />
          <BadgeWithTooltip
            label={`${t('dashboard.integrations.jobLogs.skipped')}: ${counts.skipped}`}
            tooltip={t('dashboard.integrations.jobLogs.tooltips.skipped')}
            className={cn('border', statusClass.SKIPPED)}
          />
        </div>

        {showRetry && (hasFailed || hasSkipped) && (
          <div className="flex items-center gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <Checkbox
                    checked={includeSkipped}
                    onCheckedChange={(checked) => {
                      setIncludeSkipped(checked === true);
                    }}
                    disabled={isRetrying || !hasSkipped}
                  />
                  {t('dashboard.integrations.jobLogs.includeSkipped')}
                </label>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px]">
                {t('dashboard.integrations.jobLogs.tooltips.includeSkipped')}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!canRetry || isRetrying}
                    onClick={() => {
                      void onRetry?.(job._id, includeSkipped);
                    }}
                  >
                    <IconRefresh className={cn('w-3.5 h-3.5 mr-1', isRetrying && 'animate-spin')} />
                    {isRetrying
                      ? t('dashboard.integrations.jobLogs.retrying')
                      : t('dashboard.integrations.jobLogs.retry')}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px]">
                {t('dashboard.integrations.jobLogs.tooltips.retry')}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <ul className="mt-2 space-y-1.5 max-h-48 overflow-auto">
          {errors.map((error) => {
            const reasonText = formatSyncReason(error.reason, t);
            const contactId = error.contactId ?? null;
            return (
              <li
                key={`${error.callLogId}-${error.status}`}
                className="text-xs rounded-md border border-border/60 bg-background/60 px-2 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-1"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className={cn('text-[10px] border', statusClass[error.status] ?? '')}>
                      {error.status}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[260px]">
                    {t(`dashboard.integrations.jobLogs.tooltips.${error.status}`, {
                      defaultValue: error.status,
                    })}
                  </TooltipContent>
                </Tooltip>
                <span className="font-medium">
                  {error.contactName ?? error.contactId ?? error.callLogId}
                </span>
                {reasonText != null && (
                  <span className="text-muted-foreground">{reasonText}</span>
                )}
                {contactId != null && contactId !== '' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="xs"
                        className="ml-auto h-6 w-6 p-0 shrink-0"
                        onClick={() => {
                          void handleCopyContactId(contactId);
                        }}
                        aria-label={t('dashboard.integrations.jobLogs.copyContactId')}
                      >
                        {copiedContactId === contactId
                          ? <Check className="size-3" />
                          : <Copy className="size-3" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px]">
                      {t('dashboard.integrations.jobLogs.tooltips.copyContactId')}
                    </TooltipContent>
                  </Tooltip>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
